import { isTerritorialDivision, type OrgUnitDto, type Paginated } from '@pams/shared';
import { api } from './api';

/**
 * Assembles a flat page of org units into a forest.
 *
 * The API returns the tree FLAT (`parentId` + `depth`, ordered `depth ASC, name ASC`) so
 * that it can be paginated at all — see `orgUnit.service.ts` for why a nested payload
 * cannot be. This is the other half of that decision, and it lives in one place so the
 * picker and the tree page cannot assemble it differently.
 *
 * ⚠️ DYNAMIC DEPTH. Nothing here names COMMISSIONERATE, DIVISION or PS as levels, and
 * nothing assumes three of them. The only structural fact used is `parentId`.
 */
export type OrgTreeNode = { unit: OrgUnitDto; children: OrgTreeNode[] };

/**
 * A guard against corrupt data, NOT a model of the hierarchy.
 *
 * A cycle in `parentId` — which the API's reparent cycle check makes unreachable, but
 * a direct database edit would not — would otherwise recurse until the stack blows.
 * Truncating renders a visibly wrong tree instead of a white screen.
 */
const MAX_RENDER_DEPTH = 24;

/**
 * ─── [amended WINGS.1] THE TEN TERRITORIAL DIVISIONS SORT FIRST ──────────────────
 *
 * The commissionerate gained twenty-six non-territorial divisions, and name order then
 * scattered the ten an operator actually uses to positions 10, 11, 14, 15, 20, 23, 26, 27,
 * 28 and 29 of 36 — the eight battalions alone push every one of them below the fold in a
 * picker. This lifts them back, and leaves every other level exactly where the server put
 * it.
 *
 * ⚠️ IT SORTS THE FLAT INPUT, NEVER THE ASSEMBLED ROWS, AND THE CALLERS MUST TOO.
 * `toOrgTreeRows` derives `isLastChild` from array POSITION, and that drives the
 * `--pams-tree-last` guide rail in `HierarchyTable` — so a sort applied afterwards
 * terminates the elbow on the wrong sibling. A test CAN see the flag (`orgTree.test.ts`
 * asserts it, and does); what no test can see is the rail it draws, because jsdom resolves
 * no cascade. So the flag is the tripwire, and it has to be asserted deliberately — nothing
 * about the rendering will fail on its own.
 *
 * ⚠️ SORTING THE FLAT ARRAY IS SAFE BECAUSE `buildOrgForest` IS ORDER-INDEPENDENT ABOUT
 * EVERYTHING EXCEPT SIBLINGS. It groups on `parentId` alone and detects roots with a
 * `.filter()`, neither of which reads position — so hoisting every territorial division to
 * the front of the whole array cannot move a node between parents. What survives is the
 * only thing that renders: within one parent, territorial divisions come first.
 *
 * ⚠️ THE COMPARATOR RETURNS 0 UNLESS EXACTLY ONE SIDE IS A TERRITORIAL DIVISION, AND THE
 * SORT IS STABLE (ES2019+ requires it). Two members of the same band compare equal and keep
 * the server's `depth ASC, name ASC` — which is what makes this additive rather than a second
 * ordering rule, and what keeps the ten in name order among themselves and the twenty-six in
 * name order among themselves. ⚠️ It is NOT true that only divisions move: a territorial
 * division also sorts above a station and above the commissionerate, so the FLAT array does
 * change shape. That is harmless only because every caller either feeds `buildOrgForest`,
 * which groups on `parentId`, or has already filtered to one parent's children — and
 * `orgTree.test.ts` asserts the forest is untouched rather than leaving it to this sentence.
 *
 * ⚠️ IT IS NOT INSIDE `buildOrgForest`. That function's header promises "Nothing here names
 * COMMISSIONERATE, DIVISION or PS as levels", and this names two of them. Keeping it a
 * separate function keeps that promise true — and lets a caller that must not reorder (the
 * counts map, the selected-unit lookup) simply not call it.
 */
export const sortTerritorialFirst = <T extends Pick<OrgUnitDto, 'type' | 'subType'>>(units: readonly T[]): T[] =>
  [...units].sort(
    (left, right) =>
      Number(isTerritorialDivision(right.type, right.subType)) - Number(isTerritorialDivision(left.type, left.subType)),
  );

export const buildOrgForest = (units: readonly OrgUnitDto[]): OrgTreeNode[] => {
  const byParent = new Map<string, OrgUnitDto[]>();
  for (const unit of units) {
    const key = unit.parentId ?? '__root__';
    byParent.set(key, [...(byParent.get(key) ?? []), unit]);
  }

  const build = (parentId: string, depth: number): OrgTreeNode[] => {
    if (depth > MAX_RENDER_DEPTH) return [];
    return (byParent.get(parentId) ?? []).map((unit) => ({ unit, children: build(unit.id, depth + 1) }));
  };

  /**
   * Anything whose parent is NOT in the visible set is a root for this caller.
   *
   * This is what makes a scoped tree render at all: a PS nodal's own unit has a parent
   * they are not allowed to see, so "roots are the units with no parentId" would return
   * an empty forest and their tree would silently be blank.
   */
  const visible = new Set(units.map((unit) => unit.id));
  const roots = units.filter((unit) => !unit.parentId || !visible.has(unit.parentId));

  return roots.map((unit) => ({ unit, children: build(unit.id, 1) }));
};

/**
 * The tree endpoint's own cap, from `orgUnitTreeQuerySchema` (`.max(500)`).
 *
 * Spelled out rather than written as `PAGINATION.MAX_LIMIT * 5`, which equals 500 only by
 * coincidence — the two numbers are unrelated and the coincidence would survive a change
 * to either.
 */
export const ORG_TREE_PAGE_LIMIT = 500;

/**
 * Mirrors the API's own `MAX_SUBTREE_NODES` tripwire: past this the screen stops paging
 * and says so, rather than assembling a tree nobody can read out of a payload nobody
 * wanted to download.
 */
export const ORG_TREE_MAX_PAGES = 4;

/**
 * ─── [amended OTHERS-EXPAND] EVERY PAGE OF THE CALLER'S TREE, UNDER ONE CACHE ENTRY ───────
 *
 * `pnpm expand:others` adds about a thousand `UNIT` + `SECTION` nodes as SIBLINGS of each
 * division's stations, so a super admin's scoped tree no longer fits in one page of
 * `ORG_TREE_PAGE_LIMIT`. The pickers read page one and nothing else — and the server sorts
 * `depth, name, _id`, so sections interleave by NAME with the stations at their depth and
 * what falls past row 500 is EXISTING stations, not only new sections. Nothing fails: the
 * station is simply absent from the picker, which reads as "that unit does not exist".
 *
 * ⚠️ ONE KEY, ONE MERGED ENVELOPE. `pages/OrgTree` pages with a key per page because it has
 * its own `includeInactive` key and a truncation notice to render; these consumers want neither.
 * Returning a single `Paginated` under `queryKeys.orgUnits.tree()` is what leaves every
 * consumer's `data.items`, the shared cache entry, and the tests' path-matching `get` mocks
 * exactly as they were.
 *
 * ⚠️ AND EVERY CONSUMER OF THAT KEY MUST CALL THIS, OR NONE OF THEM IS FIXED. Whichever mounts
 * first fills the entry, so one site left on a single-page `queryFn` would truncate the tree for
 * every other one, depending on render order.
 *
 * ⚠️ [amended LIST-FILTERS] SEVEN CALL SITES SHARE THE ENTRY, NOT FOUR — this said four
 * (`OrgUnitPicker`, `SourceCell`, `OfficerDrawer`, `ArrangementBuilder`) while `pages/Officers`
 * and the template decisions' `useDecisionMasters` had already joined, and `useDivisionOptions`
 * (the list filter's divisions) is the seventh. Every one passes this fetcher. The count is a
 * command, not a memory — run it before trusting the number:
 *
 *   grep -rn 'queryKey: queryKeys[.]orgUnits[.]tree()' web/src --include='*.ts' --include='*.tsx' | grep -v '[.]test[.]'
 *
 * (The bracketed dots keep the pattern from matching this comment, which holds it literally.)
 *
 * ⚠️ BOUNDED BY `ORG_TREE_MAX_PAGES`, WHICH MIRRORS `MAX_SUBTREE_NODES` (2 000 = 4 × 500).
 * Past it the tree is cut and nothing on a picker says so; `total > items.length` is the
 * signal, and the org screen is where it is rendered. The pages are disjoint because `_id`
 * makes the server's sort total (barring a write landing between two requests, which the org
 * screen's own paging shares), and they go out concurrently on the caller's one signal, so a
 * cancelled query cancels all of them.
 *
 * ⚠️ A RESPONSE WITHOUT `pages` IS ONE PAGE. The wire always carries it; a hand-built mock
 * need not, and "no page count" must never mean "ask for more".
 */
export const fetchScopedOrgTree = async (signal: AbortSignal): Promise<Paginated<OrgUnitDto>> => {
  const pageOf = (page: number): Promise<Paginated<OrgUnitDto>> =>
    api.get<Paginated<OrgUnitDto>>('/org-units/tree', { query: { page, limit: ORG_TREE_PAGE_LIMIT }, signal });

  const first = await pageOf(1);
  const reported: number | undefined = first.pages;
  const lastPage = Math.min(reported ?? 1, ORG_TREE_MAX_PAGES);
  const rest = await Promise.all(
    Array.from({ length: Math.max(lastPage - 1, 0) }, (_unused, index) => pageOf(index + 2)),
  );

  return {
    items: [...first.items, ...rest.flatMap((page) => page.items)],
    total: first.total,
    page: 1,
    limit: first.limit,
    pages: first.pages,
  };
};

/** A row the hierarchy table can render: the unit plus the tree metadata it needs. */
export type OrgTreeRow = OrgUnitDto & {
  key: string;
  /**
   * Depth RELATIVE to the rendered forest root — never the server's `unit.depth`.
   *
   * ⚠️ This distinction is the whole reason this type exists. A PS nodal's scoped tree
   * has a root at `unit.depth === 2`, so indenting by `unit.depth` would push their
   * entire tree two levels right and draw two ancestor rails to nothing. `buildOrgForest`
   * already treats "parent not in the visible set" as a root; this stays consistent with
   * that by counting from the forest, not from the database.
   */
  treeDepth: number;
  parentKey?: string;
  /** Whether this row is its parent's last child — the guide rails need it. */
  isLastChild: boolean;
  children?: OrgTreeRow[];
};

/** Forest → table rows. `treeDepth` is 0 at every forest root, whatever `unit.depth` says. */
export const toOrgTreeRows = (forest: readonly OrgTreeNode[]): OrgTreeRow[] => {
  const convert = (nodes: readonly OrgTreeNode[], treeDepth: number, parentKey?: string): OrgTreeRow[] =>
    nodes.map((node, index) => {
      const children = convert(node.children, treeDepth + 1, node.unit.id);
      return {
        ...node.unit,
        key: node.unit.id,
        treeDepth,
        isLastChild: index === nodes.length - 1,
        ...(parentKey ? { parentKey } : {}),
        // AntD switches a Table into tree mode when ANY record has a `children` key, so an
        // empty array on a leaf would give every leaf a disclosure chevron to nowhere.
        ...(children.length > 0 ? { children } : {}),
      };
    });

  return convert(forest, 0);
};

/** Depth-first, honouring an expansion set — the visual row order, for keyboard movement. */
export const flattenVisibleRows = (rows: readonly OrgTreeRow[], expanded: ReadonlySet<string>): OrgTreeRow[] =>
  rows.flatMap((row) =>
    row.children && expanded.has(row.key) ? [row, ...flattenVisibleRows(row.children, expanded)] : [row],
  );

/** Every key that has children — the expand-all target. */
export const expandableKeysOf = (rows: readonly OrgTreeRow[]): string[] =>
  rows.flatMap((row) => (row.children?.length ? [row.key, ...expandableKeysOf(row.children)] : []));

/** Expandable keys at `treeDepth < depth`, for a bounded default expansion. */
export const keysToDepth = (rows: readonly OrgTreeRow[], depth: number): string[] =>
  rows.flatMap((row) =>
    row.children?.length && row.treeDepth < depth ? [row.key, ...keysToDepth(row.children, depth)] : [],
  );

/**
 * Ids strictly beneath `id`, from a flat set of units.
 *
 * Extracted from `MoveUnitModal`, which computed it inline, so the "what is beneath this
 * node" question has one implementation shared by the move modal's blast radius and the
 * picker's disabled set.
 *
 * Exact only for the units in `units`. When the payload was truncated, or inactive units
 * were filtered out, this UNDERCOUNTS — which is why the move modal treats it as an
 * optimistic value and replaces it with the server's count.
 */
export const descendantIdsOf = (units: readonly OrgUnitDto[], id: string): string[] =>
  units.filter((candidate) => candidate.ancestors.includes(id)).map((candidate) => candidate.id);
